<template>
  <MRow :gutter="0">
    <MCol :lg="12" :sm="12" auto-size>
      <MRow>
        <MCol :size="6">
          <FlotoFormItem
            id="technician-picker"
            rules="required|nonzero"
            :label="$tc('technician')"
          >
            <FlotoTechnicianPicker
              v-model="formData.technicianId"
              as-input
              :allow-clear="false"
              :disabled="technicianIsDisabled || Boolean(formData.id)"
              :keep-unassigned="false"
              :visible-options-keys="getTechnicianOptionIds"
              mandatory-selection
            />
          </FlotoFormItem>
        </MCol>
        <MCol :size="6">
          <FlotoFormItem
            id="leave-type-picker"
            :label="$tc('leave_type')"
            rules="required|nonzero"
          >
            <LeaveTypePicker v-model="formData.leaveTypeId" as-input />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <MRow>
        <MCol :size="6">
          <FlotoFormItem
            id="start-date-picker"
            rules="required|nonzero"
            :label="`${$t('start')} ${$t('date')}`"
          >
            <FlotoDatePicker
              v-model="formData.startDate"
              :max-date="formData.endDate"
              :show-time="false"
            />
          </FlotoFormItem>
        </MCol>
        <MCol :size="6">
          <FlotoFormItem
            id="end-date-picker"
            rules="required|nonzero"
            :label="`${$t('end')} ${$t('date')}`"
          >
            <FlotoDatePicker
              v-model="formData.endDate"
              :min-date="formData.startDate"
              end-of-day
              :show-time="false"
            />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <MRow>
        <MCol :size="12">
          <FlotoFormItem
            id="remarks-input"
            v-model="formData.remarks"
            :placeholder="$t('remarks')"
            :label="$t('remarks')"
            :rows="2"
            rules="subject"
            type="textarea"
          />
        </MCol>
      </MRow>
      <MRow>
        <MCol :size="6">
          <FlotoFormItem
            v-if="isAllowedEnableDelegation"
            id="enable-work-delegation"
            class="mr-4"
            :label="$t('enable_work_delegation')"
          >
            <MSwitch
              v-model="formData.enableDelegation"
              :disabled="isLeaveDelegationActionDisabled"
              size="small"
            />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <MRow v-if="formData.enableDelegation">
        <MCol :size="12">
          <span class="text-neutral">{{ $tc('delegate_to') }}</span>
        </MCol>
      </MRow>
      <MRow v-if="formData.enableDelegation">
        <MCol :size="6">
          <FlotoFormItem
            id="technician-group"
            :label="$t('technician_group')"
            :vid="`${guid}-workDelegationGroupId`"
          >
            <FlotoTechnicianGroupPicker
              v-model="formData.workDelegationGroupId"
              as-input
              mandatory-selection
            />
          </FlotoFormItem>
        </MCol>
        <MCol :size="6">
          <FlotoFormItem
            id="assignee"
            rules="required|nonzero"
            :label="$tc('assignee')"
          >
            <FlotoTechnicianPicker
              v-model="formData.workDelegatedTo"
              as-input
              :allow-clear="false"
              :keep-unassigned="false"
              :group-id="formData.workDelegationGroupId"
              :disabled="isLeaveDelegationActionDisabled"
              :visible-options-keys="getWorkDelegateToTechnicianIds"
              change-technician-on-group-change
              mandatory-selection
            />
          </FlotoFormItem>
        </MCol>
      </MRow>
      <template v-if="formData.enableDelegation">
        <MRow>
          <MCol :size="6">
            <FlotoFormItem
              id="delegate-start-date-picker"
              rules="required|nonzero"
              :label="`${$t('delegate_start_date')}`"
            >
              <FlotoDatePicker
                v-model="formData.delegationStartDate"
                :max-date="formData.delegationEndDate"
                :min-date="formData.startDate"
                :disabled="isLeaveDelegationActionDisabled"
                :show-time="false"
              />
            </FlotoFormItem>
          </MCol>
          <MCol :size="6">
            <FlotoFormItem
              id="delegate-end-date-picker"
              rules="required|nonzero"
              :label="`${$t('delegate_end_date')}`"
            >
              <FlotoDatePicker
                v-model="formData.delegationEndDate"
                :min-date="formData.delegationStartDate"
                :max-date="formData.endDate"
                :disabled="isLeaveDelegationActionDisabled"
                end-of-day
                :show-time="false"
              />
            </FlotoFormItem>
          </MCol>
        </MRow>
        <MRow>
          <MCol :size="6">
            <FlotoFormItem
              :label="$tc('delegate_work')"
              rules="required|nonzero"
            >
              <FlotoDropdownPicker
                v-model="formData.delegatedWorkModels"
                as-input
                multiple
                searchable
                :disabled="isLeaveDelegationActionDisabled"
                :allow-clear="false"
                :options="availableModules"
              />
            </FlotoFormItem>
          </MCol>
        </MRow>
      </template>
    </MCol>
  </MRow>
</template>
<script>
import { authComputed } from '@state/modules/auth'
import LeaveTypePicker from '@components/data-picker/leave-type-picker.vue'
import { LicenseComputed } from '@/src/state/modules/license'
import { TechnicianComputed } from '@/src/state/modules/technician'
import { generateId } from '@utils/id'
export default {
  name: 'MarkLeaveForm',
  components: { LeaveTypePicker },
  props: {
    formData: { type: Object, required: true },
    resetForm: { type: Function, required: true },
    technicianIsDisabled: { type: Boolean, default: false },
  },
  data() {
    this.guid = generateId()
    return {}
  },
  computed: {
    ...authComputed,
    ...LicenseComputed,
    ...TechnicianComputed,

    // Set Technician options to mark leave for
    getTechnicianOptionIds() {
      let allowedIds = []
      if (this.user.superAdminRole) {
        allowedIds = this.technicianOptions.map((i) => i.id)
      } else {
        // Permission to mark leave for self only
        if (
          this.user.allowDelegateApproval &&
          !this.user.allowDelegateApprovalTeam
        ) {
          allowedIds = [this.user.id]
        }
        // Permission to mark leave for team only
        else if (
          this.user.allowDelegateApprovalTeam &&
          !this.user.allowDelegateApproval
        ) {
          const ids = this.technicianOptions
            .filter((i) => (i || {}).managerId === this.user.id)
            .map((a) => a.id)
          allowedIds = (ids || []).length > 0 ? ids : [0]
        }
        // Permission to mark leave for self as well as their team members
        else if (
          this.user.allowDelegateApproval &&
          this.user.allowDelegateApprovalTeam
        ) {
          allowedIds = this.technicianOptions
            .filter((i) => (i || {}).managerId === this.user.id)
            .map((a) => a.id)
          allowedIds.push(this.user.id)
        }
      }
      return allowedIds
    },
    isOnlySelfDelegationAllowed() {
      return (
        this.user.allowDelegateApproval &&
        this.user.allowWorkDelegationForSelf &&
        !this.user.allowWorkDelegationForTeam
      )
    },
    isOnlyTeamDelegationAllowed() {
      return (
        this.user.allowDelegateApprovalTeam &&
        this.user.allowWorkDelegationForTeam &&
        !this.user.allowWorkDelegationForSelf
      )
    },
    isTeamMemberOfLoggedInUser() {
      const technician = this.technicianOptions.find(
        (i) => i.id === this.formData.technicianId
      )
      return (technician || {}).managerId === this.user.id
    },

    // The technician whose leave is being marked cannot be selected for delegation action - filtered the leave technician
    getWorkDelegateToTechnicianIds() {
      let delegateToIds = this.technicianOptions
        .filter((i) => i.id !== this.formData.technicianId)
        .map((i) => i.id)
      return delegateToIds
    },

    availableModules() {
      const modules = [
        ...(this.availableModulesInLicense.indexOf(this.$constants.REQUEST) >= 0
          ? [{ text: this.$tc('request'), key: this.$constants.REQUEST }]
          : []),
        ...(this.availableModulesInLicense.indexOf(this.$constants.CHANGE) >= 0
          ? [{ text: this.$tc('change'), key: this.$constants.CHANGE }]
          : []),
      ]
      return modules
    },

    // Check for permission to allow leave delegation flag
    isAllowedEnableDelegation() {
      if (this.formData.id && this.formData.enableDelegation) {
        return true
      } else if (this.user.superAdminRole) {
        return true
      } else if (this.isOnlySelfDelegationAllowed) {
        return this.formData.technicianId === this.user.id
      } else if (this.isOnlyTeamDelegationAllowed) {
        return this.isTeamMemberOfLoggedInUser
      } else if (
        this.user.allowDelegateApproval &&
        this.user.allowWorkDelegationForSelf &&
        this.user.allowDelegateApprovalTeam &&
        this.user.allowWorkDelegationForTeam &&
        (this.formData.technicianId === this.user.id ||
          this.isTeamMemberOfLoggedInUser)
      ) {
        return true
      }
      return false
    },

    // To disable the leave delegation action when not having permission
    isLeaveDelegationActionDisabled() {
      if (!this.user.superAdminRole) {
        if (this.isOnlySelfDelegationAllowed) {
          return this.formData.technicianId !== this.user.id
        } else if (this.isOnlyTeamDelegationAllowed) {
          return !this.isTeamMemberOfLoggedInUser
        } else if (
          !this.user.allowWorkDelegationForSelf &&
          !this.user.allowWorkDelegationForTeam
        ) {
          return true
        }
      }
      return false
    },
  },
  watch: {
    'formData.startDate': {
      immediate: false,
      handler(newValue, preValue) {
        if (newValue !== preValue) {
          this.resetForm({
            ...this.formData,
            ...(this.formData.enableDelegation
              ? { delegationStartDate: newValue }
              : {}),
          })
        }
      },
    },
    'formData.endDate': {
      immediate: false,
      handler(newValue, preValue) {
        if (newValue !== preValue) {
          this.resetForm({
            ...this.formData,
            ...(this.formData.enableDelegation
              ? { delegationEndDate: newValue }
              : {}),
          })
        }
      },
    },
    'formData.enableDelegation': {
      immediate: false,
      handler(newValue, preValue) {
        if (newValue !== preValue && newValue === true) {
          this.resetForm({
            ...this.formData,
            ...(this.formData.startDate
              ? { delegationStartDate: this.formData.startDate }
              : {}),
            ...(this.formData.endDate
              ? { delegationEndDate: this.formData.endDate }
              : {}),
          })
        } else if (newValue === false) {
          this.resetForm({
            ...this.formData,
            delegationEndDate: undefined,
            delegationStartDate: undefined,
            delegatedWorkModels: undefined,
            workDelegatedTo: undefined,
            workDelegationGroupId: undefined,
          })
        }
      },
    },
  },
}
</script>
